// Aba "Financeiro" do detalhe do contrato — resumo de parcelas + ações (BM,
// liberar, emitir NF, marcar recebida, mover, excluir) + modais (Nova parcela,
// Reorganizar parcelamento, Mover parcela, Emitir NF, Link BM).
// Wave 4 (Antd) — 2026-06-01.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Table, Tag, Typography, Space, Flex, Statistic, Row, Col,
  Empty, App as AntApp, Tooltip, Modal, Form, Input, InputNumber, DatePicker,
  Select, Checkbox, Alert, List, Radio,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, SettingOutlined, FileTextOutlined, RocketOutlined,
  LinkOutlined, CheckCircleOutlined, SwapOutlined, DeleteOutlined,
  CopyOutlined, CheckOutlined, SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { hojeIso } from '../../utils/dataFmt';
import { pedirDesfazerLiberacao } from '../../utils/desfazerLiberacao';
import type { Contrato } from './types';
import { fmt, num, fmtData, getToken, _calcVencFromEmissao } from './utils';
import { derivarStatusCR } from '../financeiro-contas-receber/types';
import { SelectCliente } from '../../components/SelectCliente';

const { TextArea } = Input;

// ════════════════ ABA: FINANCEIRO (RESUMO) ════════════════

export function AbaFinanceiroResumo(props: { contrato: Contrato; onAtualizar: () => void }) {
  return <AntApp><AbaFinanceiroResumoInner {...props} /></AntApp>;
}

// ── Conciliação CR (Parte 3): mostra as parcelas FATURADAS com seus RECEBIMENTOS
// do Conta Azul pendurados embaixo (1 parcela → N recebimentos picados), + um balde
// "a conciliar" com recebimentos órfãos pra amarrar na mão. Aditivo: não mexe na
// tabela de parcelas acima. Só aparece se o contrato tem recebimento do CA envolvido.
// Separa PARCELA DO PLANO de RECEBIMENTO DO CONTA AZUL.
//
// Antes o critério era só a origem: qualquer coisa vinda do CA saía da tabela
// de parcelas. Isso escondia parcela legítima — a Medição 05 do 26006 (NF
// 2026-104, venda 1219) sumiu da tela porque o sync a CRIOU em vez de casar
// com a do Notion, e o contrato passou a acusar "diferença −R$ 12.000"
// (Lucas, 2026-07-30).
//
// O que separa de verdade: recebimento do CA não tem rótulo de parcela nem
// página no Notion — chega com a descrição do CA ("Levantamento Topográfico •
// P_xxx"). Medido em prod: dos 75 registros do CA amarrados a contrato, 74
// não têm nenhum dos dois; 616 das 654 parcelas de plano têm título. Ou seja,
// o critério devolve exatamente os 2 casos escondidos e não mexe no resto.
const _ehRecebimentoCA = (p: any) =>
  p.origem === 'conta_azul_api'
  && !(p.parcela_titulo || '').trim()
  && !p.notion_page_id;

function SecaoConciliacao({ contrato, onAtualizar }: { contrato: any; onAtualizar: () => void }) {
  const { message } = AntApp.useApp();
  const [destino, setDestino] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [verBanco, setVerBanco] = useState(false);
  const parcelas = (contrato.parcelas || []).filter((p: any) => p.status !== 'cancelada');
  const faturadas = parcelas.filter((p: any) => !p.parcela_pai_id && !_ehRecebimentoCA(p));
  // Entrada classificada como NÃO-receita (reembolso_contrato, outras_receitas…)
  // não é pendência de conciliação: fica fora do balde de órfãos. Continua no
  // detalhe do banco e no fechamento financeiro do contrato.
  const orfaos = parcelas.filter((p: any) => !p.parcela_pai_id && _ehRecebimentoCA(p)
    && (!p.tipo_receita || p.tipo_receita === 'contrato' || p.tipo_receita === 'nao_classificada'));
  const filhosPorPai: Record<string, any[]> = {};
  parcelas.forEach((p: any) => {
    if (p.parcela_pai_id) {
      if (!filhosPorPai[p.parcela_pai_id]) filhosPorPai[p.parcela_pai_id] = [];
      filhosPorPai[p.parcela_pai_id].push(p);
    }
  });
  const temFilhos = Object.keys(filhosPorPai).length > 0;
  if (orfaos.length === 0 && !temFilhos) return null;  // nada de CA → não mostra

  async function chamar(action: string, body: any, ok: string, busyId: string) {
    setBusy(busyId);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...body }, { headers: { Authorization: token } });
      message.success(ok);
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setBusy(null); }
  }
  const conciliar = (recebimento_id: string, parcela_id: string) => {
    if (!parcela_id) { message.warning('Escolha a parcela faturada.'); return; }
    chamar('fin_conciliar_recebimento', { recebimento_id, parcela_id }, 'Recebimento conciliado.', recebimento_id);
  };
  const desconciliar = (recebimento_id: string) =>
    chamar('fin_desconciliar_recebimento', { recebimento_id }, 'Desfeito — voltou pra "a conciliar".', recebimento_id);

  // Recebido da parcela = filhos conciliados, ou o que ela já tinha marcado recebido.
  const recebidoDe = (p: any) => (Number(p.qtd_recebimentos) > 0 ? num(p.recebido_filhos) : num(p.valor_recebido || 0));
  const statusConc = (p: any): { txt: string; color: string } => {
    const rec = recebidoDe(p); const prev = num(p.valor_previsto);
    if (rec <= 0) return { txt: 'aberto', color: 'default' };
    if (rec + 0.01 < prev) return { txt: 'parcial', color: 'orange' };
    return { txt: 'conciliado', color: 'green' };
  };
  const nomeP = (p: any) => p.descricao || p.parcela_titulo || '(sem nome)';

  return (
    <Card size="small" style={{ marginTop: 16 }}
      title={<Space><LinkOutlined />Conciliação de recebimentos</Space>}
      extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>parcela faturada × recebimentos do Conta Azul</Typography.Text>}>
      {faturadas.length > 0 && (
        <List size="small" dataSource={faturadas} rowKey="id"
          renderItem={(p: any) => {
            const filhos = filhosPorPai[p.id] || [];
            const st = statusConc(p);
            return (
              <List.Item style={{ display: 'block', padding: '8px 0' }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
                  <Typography.Text strong>{nomeP(p)}</Typography.Text>
                  <Space size="small">
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      bruto {fmt(p.valor_previsto)} · recebido {fmt(recebidoDe(p))}
                    </Typography.Text>
                    <Tag color={st.color}>{st.txt}</Tag>
                  </Space>
                </Flex>
                {filhos.map((f: any) => (
                  <Flex key={f.id} justify="space-between" align="center" style={{ paddingLeft: 18, marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                      ↳ {fmtData(f.data_recebimento || f.data_vencimento)} · {nomeP(f)}
                    </Typography.Text>
                    <Space size="small">
                      <Typography.Text style={{ fontSize: 13 }}>{fmt(f.valor_recebido)}</Typography.Text>
                      <Button size="small" type="text" danger loading={busy === f.id} onClick={() => desconciliar(f.id)}>desamarrar</Button>
                    </Space>
                  </Flex>
                ))}
              </List.Item>
            );
          }} />
      )}
      {orfaos.length > 0 && (() => {
        const orfaosTotal = orfaos.reduce((s: number, o: any) => s + num(o.valor_recebido || 0), 0);
        const LIM = 25;
        return (
        <div style={{ marginTop: 12, paddingTop: 8 }}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Recebimentos do banco (Conta Azul): {orfaos.length} · {fmt(orfaosTotal)} — detalhe pra conferir com o extrato
            </Typography.Text>
            <Button size="small" type="link" onClick={() => setVerBanco(v => !v)}>
              {verBanco ? 'ocultar' : 'ver / amarrar a um BM'}
            </Button>
          </Flex>
          {verBanco && (<>
          {faturadas.length === 0 && (
            <Alert type="info" showIcon style={{ marginTop: 8, marginBottom: 8 }}
              message="Este contrato ainda não tem parcelas faturadas — os recebimentos do CA aparecem só como detalhe do banco." />
          )}
          <List size="small" dataSource={orfaos.slice(0, LIM)} rowKey="id"
            renderItem={(o: any) => (
              <List.Item style={{ padding: '6px 0' }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ width: '100%' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    {fmtData(o.data_recebimento || o.data_vencimento)} · {nomeP(o)} · <strong>{fmt(o.valor_recebido)}</strong>
                  </Typography.Text>
                  <Space size="small">
                    <Select size="small" style={{ minWidth: 200 }} placeholder="amarrar à parcela…" showSearch optionFilterProp="label"
                      value={destino[o.id]} onChange={(v) => setDestino({ ...destino, [o.id]: v })}
                      disabled={faturadas.length === 0}
                      options={faturadas.map((f: any) => ({ value: f.id, label: nomeP(f) }))} />
                    <Button size="small" type="primary" loading={busy === o.id}
                      disabled={!destino[o.id]} onClick={() => conciliar(o.id, destino[o.id])}>amarrar</Button>
                  </Space>
                </Flex>
              </List.Item>
            )} />
          {orfaos.length > LIM && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              + {orfaos.length - LIM} recebimento(s) a mais.
            </Typography.Text>
          )}
          </>)}
        </div>
        );
      })()}
    </Card>
  );
}

function AbaFinanceiroResumoInner({ contrato, onAtualizar }: { contrato: Contrato; onAtualizar: () => void }) {
  const { message, modal } = AntApp.useApp();
  const parcelasRaw = contrato.parcelas || [];
  // Conciliação CR (modelo B): a lista/previsto = parcelas FATURADAS (o plano,
  // origem != Conta Azul). Os recebimentos do CA (origem=CA) NÃO são parcelas —
  // sobem direto pro RECEBIDO do contrato (sejam filhos já conciliados ou órfãos
  // "a conciliar"). Aparecem só no card "Conciliação de recebimentos".
  const recebimentosCA = parcelasRaw.filter((p: any) => _ehRecebimentoCA(p) && p.status !== 'cancelada');
  // Status EFETIVO (mesma derivação da lista do Financeiro, derivarStatusCR):
  // aqui o payload mostrava o status cru do banco — uma 'atrasada' gravada com
  // vencimento futuro ficava vermelha pra sempre, e pendente vencida não
  // avisava. Deriva uma vez; coluna e botões passam a enxergar igual às
  // outras telas (Lucas 2026-08-05).
  const hojeStatus = hojeIso();
  const parcelas = parcelasRaw
    .filter((p: any) => !p.parcela_pai_id && !_ehRecebimentoCA(p))
    .map((p: any) => {
      const s = derivarStatusCR(p, hojeStatus);
      return s === p.status ? p : { ...p, status: s };
    });
  // recebido de cada parcela/BM = o que ela já recebeu (marcado no sistema, ou os
  // recebimentos do CA já amarrados nela). Os recebimentos soltos do CA são detalhe
  // do banco (card abaixo, recolhido) — pra bater com o extrato, NÃO somam aqui.
  // Filho que soma ZERO não pode APAGAR o recebido próprio da mãe (2026-08-06):
  // um espelho do CA pendurado e nunca alimentado — o sync está pausado desde
  // 15/07 — zerava o recebido da parcela nesta tela, enquanto a listagem do CR
  // mostrava certo. A listagem usa COALESCE(NULLIF(soma,0), valor_recebido);
  // aqui bastava `qtd > 0`. Mesma regra nas duas telas agora.
  const recEf = (p: any) => {
    const somaFilhos = num(p.recebido_filhos);
    return somaFilhos > 0 ? somaFilhos : num(p.valor_recebido || 0);
  };
  // Canceladas NÃO entram nos totais (senão inflam previsto/em-aberto). Continuam
  // aparecendo na lista, mas não contam pra soma nem pro "em aberto".
  const ativas = parcelas.filter((p: any) => p.status !== 'cancelada');
  const totalPrevisto = ativas.reduce((s, p) => s + (p.valor_previsto || 0), 0);
  const totalRecebido = ativas.reduce((s, p) => s + recEf(p), 0);
  const totalAberto = totalPrevisto - totalRecebido;
  const valorEntregue = contrato.entregaveis_valor_entregue || 0;
  const valorTotal = num(contrato.valor_contratado) + num(contrato.aditivos_valor_soma);
  const pctEntregue = valorTotal > 0 ? (valorEntregue / valorTotal) * 100 : 0;
  const pctRecebido = valorTotal > 0 ? (totalRecebido / valorTotal) * 100 : 0;
  const [emitindoNf, setEmitindoNf] = useState<any>(null);
  const [linkBm, setLinkBm] = useState<{ url: string; nome: string } | null>(null);
  const [criandoNova, setCriandoNova] = useState(false);
  const [movendoParcela, setMovendoParcela] = useState<any>(null);
  const [reorganizando, setReorganizando] = useState(false);

  async function criarParcela(dados: any) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_criar_parcela', contrato_id: contrato.id, ...dados },
        { headers: { Authorization: token } });
      message.success('Parcela criada.');
      setCriandoNova(false);
      onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  function pedirExcluir(p: any) {
    modal.confirm({
      title: `Excluir a parcela "${p.descricao}" definitivamente?`,
      content: `Valor: ${fmt(p.valor_previsto)}. Esta ação fica registrada na linha do tempo.`,
      okText: 'Excluir', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`,
            { action: 'fin_excluir_parcela', id: p.id },
            { headers: { Authorization: token } });
          message.success('Excluída.');
          onAtualizar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  function pedirGerarBm(p: any) {
    modal.confirm({
      title: `Gerar BM (Boletim de Medição) pra "${p.descricao}"?`,
      content: `Valor ${fmt(p.valor_previsto)}. Vai gerar link público pra cliente assinar. Após assinatura, libera pra emissão de NF.`,
      onOk: async () => {
        try {
          const token = await getToken();
          const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'op_gerar_bm', parcela_id: p.id,
          }, { headers: { Authorization: token } });
          const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
          setLinkBm({
            url: `${window.location.origin}${d.url_publica}`,
            nome: p.descricao || p.parcela_titulo || '',
          });
          onAtualizar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  function mostrarLinkBm(p: any) {
    if (!p.bm_link_hash) return;
    setLinkBm({
      url: `${window.location.origin}/publico/bm/${p.bm_link_hash}`,
      nome: p.descricao || p.parcela_titulo || '',
    });
  }

  function pedirLiberar(p: any) {
    modal.confirm({
      title: `Liberar pra faturamento "${p.descricao}"?`,
      content: `Valor ${fmt(p.valor_previsto)}. Use pra parcelas administrativas (aprovação/êxito) que não passam por BM, ou quando o cliente já confirmou off-line.`,
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'fin_liberar_parcela', id: p.id,
          }, { headers: { Authorization: token } });
          message.success('Liberada.');
          onAtualizar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  function pedirDesfazer(p: any) {
    // Liberou por engano: desfaz E avisa quem emite a nota (30/09/2026).
    pedirDesfazerLiberacao({ modal, message, parcela: p, onFeito: onAtualizar });
  }

  function pedirReceber(p: any) {
    let valorRecebido = p.valor_previsto;
    modal.confirm({
      title: `Confirma recebimento de "${p.descricao}"?`,
      content: (
        <div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 11 }}>
            Valor previsto: {fmt(p.valor_previsto)}
          </Typography.Paragraph>
          <InputNumber defaultValue={p.valor_previsto} min={0} step={100}
            style={{ width: '100%' }} placeholder="Valor recebido"
            onChange={(v) => { valorRecebido = v; }} />
        </div>
      ),
      okText: 'Confirmar',
      onOk: async () => {
        if (!Number.isFinite(valorRecebido) || valorRecebido <= 0) {
          message.warning('Valor inválido');
          return Promise.reject();
        }
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'fin_atualizar_conta_receber',
            id: p.id, valor_recebido: valorRecebido,
            data_recebimento: hojeIso(),
            status: 'recebida',
          }, { headers: { Authorization: token } });
          message.success('Recebimento registrado.');
          onAtualizar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const tipoEmoji = (t?: string) =>
    t === 'mobilizacao' ? '🚀' : t === 'medicao' ? '📐' : t === 'saldo' ? '✓' : t ? '•' : '';

  const colunas: ColumnsType<any> = [
    { title: '#', dataIndex: 'numero_parcela', key: 'n', width: 40, align: 'center',
      render: (n) => <Typography.Text strong>{n || '—'}</Typography.Text> },
    { title: 'Parcela', key: 'tit',
      render: (_, p) => (
        <div>
          <Typography.Text strong>
            {p.tipo_parcela && <span style={{ marginRight: 6, fontSize: 10 }}>{tipoEmoji(p.tipo_parcela)}</span>}
            {p.parcela_titulo || p.descricao || '—'}
          </Typography.Text>
          <Space size={4} style={{ marginLeft: 6 }}>
            {p.eh_sub_os && p.contrato_numero && (
              <Tag color="orange" style={{ fontFamily: 'monospace', fontSize: 9 }}>{p.contrato_numero}</Tag>
            )}
            {p.liberada && p.status === 'pendente' && <Tag color="purple" style={{ fontSize: 9 }}>LIBERADA</Tag>}
            {(p as any).criada_via_financeiro && (
              <Tag color="gold" style={{ fontSize: 9 }}
                title="Criada pela tela de Contas a Receber — encaixar no plano de parcelas (Reorganizar parcelas)">
                ⚠ REORGANIZAR
              </Tag>
            )}
            {(p as any).negocio_id && (
              <Tag color="blue" style={{ fontSize: 9, cursor: 'pointer' }}
                title={`Abrir a venda de origem (negócio #${(p as any).negocio_id}) — documento de venda, PDFs e anexos`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Mesmo fluxo do "Ver proposta" das Licitações (L1 fix):
                  // sessionStorage + evento específico + app-navegar pro Pipeline.
                  sessionStorage.setItem('abrir_proposta_id', String((p as any).negocio_id));
                  window.dispatchEvent(new CustomEvent('abrir-proposta-no-pipeline', {
                    detail: { propostaId: (p as any).negocio_id },
                  }));
                  window.dispatchEvent(new CustomEvent('app-navigate', {
                    detail: { modulo: 'comercial', pagina: 'com-pipeline' },
                  }));
                }}>
                📄 VENDA
              </Tag>
            )}
          </Space>
        </div>
      ),
    },
    { title: 'Emissão', key: 'em', width: 110, align: 'center',
      render: (_, p) => p.nota_fiscal_emissao
        ? <Typography.Text type="secondary">{fmtData(p.nota_fiscal_emissao)}</Typography.Text>
        : (
          <Tooltip title="Ainda não faturada — exibindo vencimento">
            <Typography.Text type="secondary" style={{ fontStyle: 'italic' }}>
              {p.data_vencimento ? fmtData(p.data_vencimento) : '—'} <span style={{ fontSize: 8 }}>(venc)</span>
            </Typography.Text>
          </Tooltip>
        ),
    },
    { title: 'Previsto', dataIndex: 'valor_previsto', key: 'v', width: 110, align: 'right',
      render: (v) => <Typography.Text strong>{fmt(v)}</Typography.Text> },
    { title: 'BM', dataIndex: 'bm_status', key: 'bm', width: 110, align: 'center',
      render: (s) => s === 'assinado_cliente' ? <Tag color="green">✓ Assinado</Tag>
        : s === 'gerado' ? <Tag color="purple">📋 Gerado</Tag>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    { title: 'Status', dataIndex: 'status', key: 's', width: 90, align: 'center',
      render: (s) => <Typography.Text type="secondary" style={{ fontSize: 10 }}>{s}</Typography.Text> },
    { title: 'NF', dataIndex: 'nota_fiscal_numero', key: 'nf', width: 80,
      render: (v) => <Typography.Text type="secondary" style={{ fontSize: 10 }}>{v || '—'}</Typography.Text> },
    { title: 'Ação', key: 'acao', width: 260, align: 'center',
      render: (_, p) => (
        <Space size={4} wrap>
          {p.status === 'pendente' && !p.liberada && !p.bm_link_hash && (
            <>
              <Button size="small" type="primary"
                style={{ background: '#7C3AED', borderColor: '#7C3AED' }}
                onClick={() => pedirGerarBm(p)} title="Gerar BM">📐 BM</Button>
              <Button size="small" icon={<RocketOutlined />}
                style={{ borderColor: '#B07500', color: '#B07500' }}
                onClick={() => pedirLiberar(p)} title="Liberar sem BM">Liberar</Button>
            </>
          )}
          {p.status === 'pendente' && !p.liberada && p.bm_link_hash && p.bm_status === 'gerado' && (
            <>
              <Button size="small" icon={<LinkOutlined />}
                style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                onClick={() => mostrarLinkBm(p)} title="Mostrar link BM">Link</Button>
              <Button size="small" icon={<RocketOutlined />}
                style={{ borderColor: '#B07500', color: '#B07500' }}
                onClick={() => pedirLiberar(p)}>Liberar</Button>
            </>
          )}
          {p.status === 'pendente' && p.liberada && (
            <>
              <Button size="small" type="primary" icon={<FileTextOutlined />}
                onClick={() => setEmitindoNf(p)}>Emitir NF</Button>
              {!p.nota_fiscal_numero && (
                <Button size="small" danger onClick={() => pedirDesfazer(p)}
                  title="Desfazer a liberação e avisar quem emite a NF">Desfazer</Button>
              )}
            </>
          )}
          {(p.status === 'faturada' || p.status === 'atrasada') && (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
              style={{ background: p.status === 'atrasada' ? '#B91626' : '#0a7f4a',
                borderColor: p.status === 'atrasada' ? '#B91626' : '#0a7f4a' }}
              onClick={() => pedirReceber(p)}>Receber</Button>
          )}
          <Tooltip title="Mover pra outro contrato">
            <Button size="small" icon={<SwapOutlined />} onClick={() => setMovendoParcela(p)} />
          </Tooltip>
          {p.status !== 'recebida' && (
            <Tooltip title="Excluir parcela">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => pedirExcluir(p)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 14, background: '#FAFBFF' }}
        title={<Typography.Text strong style={{ fontSize: 11, color: '#888',
          textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Posição financeira do contrato
        </Typography.Text>}>
        <Row gutter={18}>
          <Col xs={12} md={6}>
            <Statistic title="Contratado total" value={valorTotal} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18 }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>base + aditivos</Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Entregue" value={valorEntregue} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18, color: '#0a7f4a' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>{pctEntregue.toFixed(0)}% do total</Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Recebido" value={totalRecebido} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18, color: '#0930D0' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>{pctRecebido.toFixed(0)}% do total</Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic title="Em aberto" value={totalAberto} prefix="R$" precision={0}
              valueStyle={{ fontSize: 18, color: '#B07500' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {parcelas.filter(p => p.status !== 'recebida' && p.status !== 'cancelada').length} parcela(s)
            </Typography.Text>
          </Col>
        </Row>
      </Card>

      <Flex align="center" gap={8} style={{ marginBottom: 8 }}>
        <Typography.Text strong style={{ flex: 1, color: '#020D7F', fontSize: 12 }}>
          {parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''}
          {parcelas.length > 0 && (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                · soma {fmt(totalPrevisto)} / contratado {fmt(valorTotal)}
              </Typography.Text>
              {Math.abs(totalPrevisto - valorTotal) > 0.01 && (
                <Tag color="red" style={{ marginLeft: 6, fontSize: 10 }}>
                  ⚠ diferença {fmt(totalPrevisto - valorTotal)}
                </Tag>
              )}
            </>
          )}
        </Typography.Text>
        <Button icon={<SettingOutlined />} onClick={() => setReorganizando(true)}
          style={{ borderColor: '#7C3AED', color: '#7C3AED' }}>
          Reorganizar parcelamento
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCriandoNova(true)}>
          Nova parcela
        </Button>
      </Flex>

      {parcelas.length > 0 ? (
        <Table size="small" rowKey="id" columns={colunas} dataSource={parcelas} pagination={false} />
      ) : (
        <Empty description="Nenhuma parcela cadastrada neste contrato. Use '+ Nova parcela' acima pra adicionar." />
      )}

      <SecaoConciliacao contrato={contrato} onAtualizar={onAtualizar} />

      {criandoNova && <ModalNovaParcela onSalvar={criarParcela} onClose={() => setCriandoNova(false)} />}
      {movendoParcela && (
        <ModalMoverParcela parcela={movendoParcela} contratoAtual={contrato}
          onSucesso={() => { setMovendoParcela(null); onAtualizar(); }}
          onClose={() => setMovendoParcela(null)} />
      )}
      {reorganizando && (
        <ModalReorganizarParcelas contrato={contrato} valorAlvo={valorTotal}
          onSucesso={() => { setReorganizando(false); onAtualizar(); }}
          onClose={() => setReorganizando(false)} />
      )}
      {emitindoNf && (
        <ModalEmitirNfProjeto parcela={emitindoNf}
          onClose={() => setEmitindoNf(null)}
          onSucesso={() => { setEmitindoNf(null); onAtualizar(); }} />
      )}
      {linkBm && <ModalLinkBmProjeto link={linkBm} onClose={() => setLinkBm(null)} />}
    </div>
  );
}

// ════════════════ MODAL: EMITIR NF ════════════════

function ModalEmitirNfProjeto({ parcela, onClose, onSucesso }: {
  parcela: any; onClose: () => void; onSucesso: () => void;
}) {
  return <AntApp><ModalEmitirNfInner parcela={parcela} onClose={onClose} onSucesso={onSucesso} /></AntApp>;
}

function ModalEmitirNfInner({ parcela, onClose, onSucesso }: {
  parcela: any; onClose: () => void; onSucesso: () => void;
}) {
  const hoje = hojeIso();
  const [form, setForm] = useState({
    nf_numero: '', nf_emitida_em: hoje,
    valor_previsto: parcela.valor_previsto ?? null,
    data_vencimento: parcela.data_vencimento || hoje,
    meio_pagamento: parcela.meio_pagamento || 'boleto',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function salvar() {
    setErro('');
    if (!form.nf_numero.trim()) { setErro('Número da NF é obrigatório'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_emitir_nf_parcela',
        parcela_id: parcela.id,
        nf_numero: form.nf_numero.trim(),
        nf_emitida_em: form.nf_emitida_em || null,
        valor_previsto: form.valor_previsto != null ? form.valor_previsto : undefined,
        data_vencimento: form.data_vencimento || undefined,
        meio_pagamento: form.meio_pagamento || null,
      }, { headers: { Authorization: token } });
      onSucesso();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} width={540}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <FileTextOutlined /> Emitir NF
      </Typography.Title>}
      onOk={salvar} confirmLoading={salvando}
      okText="Emitir NF" okButtonProps={{ disabled: !form.nf_numero.trim() }}>
      <Card size="small" style={{ background: '#FAFBFF', marginBottom: 14 }}>
        <Typography.Text strong>{parcela.parcela_titulo || parcela.descricao}</Typography.Text>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Valor: {fmt(parcela.valor_previsto)} · Venc: {fmtData(parcela.data_vencimento)}
          </Typography.Text>
        </div>
      </Card>
      <Form layout="vertical" disabled={salvando}>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Número da NF *" required>
              <Input value={form.nf_numero} autoFocus
                onChange={e => setForm({ ...form, nf_numero: e.target.value })}
                placeholder="Ex.: 12345" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Emissão">
              <DatePicker value={form.nf_emitida_em ? dayjs(form.nf_emitida_em) : null}
                onChange={(d) => setForm({ ...form, nf_emitida_em: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Valor">
              <InputNumber value={form.valor_previsto}
                onChange={(v) => setForm({ ...form, valor_previsto: v as number | null })}
                style={{ width: '100%' }} step={100}
                formatter={(val) => `R$ ${(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                parser={(val) => Number(String(val).replace(/[^\d.-]/g, '')) as any} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Vencimento">
              <DatePicker value={form.data_vencimento ? dayjs(form.data_vencimento) : null}
                onChange={(d) => setForm({ ...form, data_vencimento: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        {erro && <Alert type="error" message={erro} showIcon />}
      </Form>
    </Modal>
  );
}

// ════════════════ MODAL: NOVA PARCELA ════════════════

function ModalNovaParcela({ onSalvar, onClose }: {
  onSalvar: (dados: any) => void; onClose: () => void;
}) {
  return <AntApp><ModalNovaParcelaInner onSalvar={onSalvar} onClose={onClose} /></AntApp>;
}

function ModalNovaParcelaInner({ onSalvar, onClose }: {
  onSalvar: (dados: any) => void; onClose: () => void;
}) {
  const hoje = hojeIso();
  const [form, setForm] = useState({
    parcela_titulo: '', valor_previsto: null as number | null,
    data_vencimento: hoje,
    tipo_parcela: '', meio_pagamento: '',
  });
  const [salvando, setSalvando] = useState(false);

  const podeSalvar = form.parcela_titulo.trim() && form.valor_previsto != null && form.valor_previsto > 0;

  async function salvar() {
    setSalvando(true);
    try {
      await onSalvar({
        parcela_titulo: form.parcela_titulo.trim(),
        descricao: form.parcela_titulo.trim(),
        valor_previsto: form.valor_previsto,
        data_vencimento: form.data_vencimento || null,
        tipo_parcela: form.tipo_parcela || null,
        meio_pagamento: form.meio_pagamento || null,
      });
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} width={520}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <PlusOutlined /> Nova parcela
      </Typography.Title>}
      onOk={salvar} confirmLoading={salvando} okText="Criar parcela"
      okButtonProps={{ disabled: !podeSalvar }}>
      <Form layout="vertical" disabled={salvando}>
        <Form.Item label="Título / descrição *" required>
          <Input value={form.parcela_titulo} autoFocus
            onChange={e => setForm({ ...form, parcela_titulo: e.target.value })}
            placeholder="Ex.: BM 02 — entrega laudo hidrológico" />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Valor previsto (R$) *" required>
              <InputNumber value={form.valor_previsto}
                onChange={(v) => setForm({ ...form, valor_previsto: v as number | null })}
                style={{ width: '100%' }} step={100} placeholder="10000.00"
                formatter={(val) => val != null ? `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                parser={(val) => Number(String(val).replace(/[^\d.-]/g, '')) as any} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Vencimento">
              <DatePicker value={form.data_vencimento ? dayjs(form.data_vencimento) : null}
                onChange={(d) => setForm({ ...form, data_vencimento: d ? d.format('YYYY-MM-DD') : '' })}
                format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Tipo">
              <Select value={form.tipo_parcela || undefined}
                onChange={(v) => setForm({ ...form, tipo_parcela: v || '' })}
                placeholder="— escolha —" allowClear
                options={[
                  { value: 'mobilizacao', label: '🚀 Mobilização' },
                  { value: 'medicao', label: '📐 Medição' },
                  { value: 'saldo', label: '✓ Saldo' },
                  { value: 'avulsa', label: '• Avulsa' },
                ]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Meio de pagamento">
              <Select value={form.meio_pagamento || undefined}
                onChange={(v) => setForm({ ...form, meio_pagamento: v || '' })}
                placeholder="— escolha —" allowClear
                options={[
                  { value: 'boleto', label: 'Boleto' },
                  { value: 'pix', label: 'PIX' },
                  { value: 'transferencia', label: 'Transferência' },
                  { value: 'ted', label: 'TED' },
                ]} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ════════════════ MODAL: REORGANIZAR PARCELAMENTO ════════════════

interface LinhaParcela {
  id?: string;
  parcela_titulo: string;
  valor_previsto: string;
  nota_fiscal_emissao: string;
  prazo_dias: number;
  data_vencimento: string;
  tipo_parcela: string;
  meio_pagamento: string;
  numero_parcela: number;
  travada?: boolean;
  status?: string;
  fixada?: boolean;
  // multi-CNPJ (2026-07-07): pagador da parcela quando difere do cliente do
  // contrato (ex.: 24053 — laudo rateado entre 3 CNPJs contratantes)
  cliente_id?: number | null;
  // NF-CNAE (2026-07-27): código de serviço da NF da parcela (editável aqui e no CR)
  cnae?: string | null;
  // FAMÍLIA (2026-08-04): contrato DONO da linha. Numa reorganização do pai as
  // parcelas dos filhos aparecem juntas, e cada uma tem que voltar pro seu.
  contrato_id?: string | null;
  contrato_numero?: string | null;
}

function ModalReorganizarParcelas(props: {
  contrato: Contrato; valorAlvo: number;
  onSucesso: () => void; onClose: () => void;
}) {
  return <AntApp><ModalReorganizarParcelasInner {...props} /></AntApp>;
}

function ModalReorganizarParcelasInner({ contrato, valorAlvo, onSucesso, onClose }: {
  contrato: Contrato; valorAlvo: number;
  onSucesso: () => void; onClose: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  // Só parcelas do PLANO entram no reorganizador: linhas espelho do Conta Azul
  // e filhos de conciliação NÃO são parcelas (mexer nelas aqui apagaria o
  // espelho — o backend também se protege, mas a UI nem mostra).
  // FAMÍLIA (2026-08-04): as parcelas dos contratos-filho entram aqui. O
  // "Valor contratado total" do cabeçalho SEMPRE somou pai + filhos, mas a
  // lista só trazia as do próprio contrato — o modal comparava coisas
  // diferentes. Na Ceriluz (HY26015) dava R$ 27.000 de alvo contra 0 parcelas;
  // no HY24023, R$ 649.056,30 contra 0. Cada linha carrega o contrato dono e o
  // backend nunca o altera, então reorganizar o pai não puxa as parcelas dos
  // filhos pra ele.
  const proprias = (contrato.parcelas || []).filter((p: any) =>
    !p.parcela_pai_id && p.origem !== 'conta_azul_api');
  // Parcela do plano que veio do CA (tem rótulo ou página do Notion) aparece na
  // TABELA mas fica fora daqui de propósito: o backend do reorganizar apaga e
  // recria só o que não é do CA, então incluí-la aqui criaria uma cópia — a
  // original sobreviveria ao DELETE e a linha voltaria como parcela nova.
  // Enquanto isso não for alinhado nos dois lados, avisa em vez de sumir calado.
  const foraDoReorganizador = (contrato.parcelas || []).filter((p: any) =>
    !p.parcela_pai_id && p.origem === 'conta_azul_api'
    && ((p.parcela_titulo || '').trim() || p.notion_page_id));
  const PRAZO_DEFAULT = 30;
  // A lista de clientes vive dentro do SelectCliente (mesmo cache de 10 min).
  const [linhas, setLinhas] = useState<LinhaParcela[]>(() =>
    proprias.map((p, i) => {
      const emissao = p.nota_fiscal_emissao ? String(p.nota_fiscal_emissao).slice(0, 10) : '';
      const vencReal = p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : '';
      // FIX (2026-06-23): se prazo_dias não está salvo, deriva do gap REAL
      // emissão→vencimento. Antes caía no default 30 e RECALCULAVA o vencimento
      // como emissão+30, sobrescrevendo a data real ao salvar (caso HY26037).
      let prazoSrc: number;
      if (p.prazo_dias && p.prazo_dias > 0) prazoSrc = p.prazo_dias;
      else if (/^\d{4}-\d{2}-\d{2}$/.test(emissao) && /^\d{4}-\d{2}-\d{2}$/.test(vencReal)) {
        const diff = Math.round((new Date(vencReal + 'T12:00:00').getTime() - new Date(emissao + 'T12:00:00').getTime()) / 86400000);
        prazoSrc = diff >= 0 ? diff : PRAZO_DEFAULT;
      } else prazoSrc = PRAZO_DEFAULT;
      let emissaoEfetiva = emissao;
      if (!emissaoEfetiva && vencReal) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(vencReal)) {
          const dt = new Date(vencReal + 'T12:00:00');
          dt.setDate(dt.getDate() - prazoSrc);
          emissaoEfetiva = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
        }
      }
      const venc = _calcVencFromEmissao(emissaoEfetiva, prazoSrc);
      return {
        id: p.id,
        parcela_titulo: p.parcela_titulo || p.descricao || `Parcela ${i + 1}`,
        valor_previsto: String(p.valor_previsto || ''),
        nota_fiscal_emissao: emissaoEfetiva,
        prazo_dias: prazoSrc,
        data_vencimento: venc || (p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : ''),
        tipo_parcela: p.tipo_parcela || '',
        meio_pagamento: p.meio_pagamento || '',
        numero_parcela: p.numero_parcela || (i + 1),
        travada: p.status === 'recebida' && !!p.valor_recebido,
        status: p.status,
        cliente_id: p.cliente_id ?? null,
        cnae: p.cnae ?? null,
        // eh_sub_os = a parcela é de um contrato-filho; contrato_numero diz qual.
        contrato_id: (p as any).contrato_id ?? null,
        contrato_numero: (p as any).eh_sub_os ? ((p as any).contrato_numero ?? null) : null,
      };
    })
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  function setCampo(idx: number, campo: keyof LinhaParcela, valor: any) {
    setLinhas(curr => {
      const nv = [...curr];
      const atual = { ...nv[idx], [campo]: valor };
      if (campo === 'nota_fiscal_emissao' || campo === 'prazo_dias') {
        atual.data_vencimento = _calcVencFromEmissao(atual.nota_fiscal_emissao, atual.prazo_dias);
      }
      nv[idx] = atual;
      return nv;
    });
  }

  function setPct(idx: number, pct: number | null) {
    if (pct == null || pct < 0) return;
    const novoValor = (pct * valorAlvo / 100).toFixed(2);
    setCampo(idx, 'valor_previsto', novoValor);
  }

  function rebalancearNaoFixas() {
    const recebidasSoma = linhas.filter(l => l.travada).reduce((s, l) => s + (parseFloat(l.valor_previsto) || 0), 0);
    const fixadasSoma = linhas.filter(l => l.fixada && !l.travada).reduce((s, l) => s + (parseFloat(l.valor_previsto) || 0), 0);
    const naoFixas = linhas.filter(l => !l.travada && !l.fixada);
    if (naoFixas.length === 0) { message.warning('Não há parcelas livres para rebalancear.'); return; }
    const restante = valorAlvo - recebidasSoma - fixadasSoma;
    if (restante < 0) { message.error(`Recebidas + fixadas > contratado. Reduza alguma fixa antes.`); return; }
    const valorCada = Math.floor(restante / naoFixas.length * 100) / 100;
    const sobra = restante - (valorCada * naoFixas.length);
    setLinhas(curr => {
      let primeiroIdx = -1;
      return curr.map((l, i) => {
        if (l.travada || l.fixada) return l;
        if (primeiroIdx < 0) primeiroIdx = i;
        const v = i === primeiroIdx ? (valorCada + sobra).toFixed(2) : valorCada.toFixed(2);
        return { ...l, valor_previsto: v };
      });
    });
  }

  function adicionar() {
    const proxNum = Math.max(0, ...linhas.map(l => l.numero_parcela || 0)) + 1;
    setLinhas(curr => [...curr, {
      parcela_titulo: `Parcela ${proxNum}`,
      valor_previsto: '', nota_fiscal_emissao: '', prazo_dias: PRAZO_DEFAULT,
      data_vencimento: '', tipo_parcela: '', meio_pagamento: '', numero_parcela: proxNum,
      cliente_id: null,
      // NF-CNAE: parcela nova herda o código das irmãs (1 código por NF em geral)
      cnae: curr.find(l => l.cnae)?.cnae ?? null,
    }]);
  }

  function remover(idx: number) {
    if (linhas[idx].travada) { message.warning('Parcela já recebida não pode ser removida.'); return; }
    setLinhas(curr => curr.filter((_, i) => i !== idx));
  }

  function dividirIgualmente() {
    if (linhas.length === 0) return;
    const recebidasSoma = linhas.filter(l => l.travada).reduce((s, l) => s + (parseFloat(l.valor_previsto) || 0), 0);
    const naoTravadas = linhas.filter(l => !l.travada);
    if (naoTravadas.length === 0) return;
    const restante = valorAlvo - recebidasSoma;
    const valorCada = Math.floor(restante / naoTravadas.length * 100) / 100;
    const sobra = restante - (valorCada * naoTravadas.length);
    setLinhas(curr => {
      let primeiroNaoTravado = -1;
      return curr.map((l, i) => {
        if (l.travada) return l;
        if (primeiroNaoTravado < 0) primeiroNaoTravado = i;
        const v = i === primeiroNaoTravado ? (valorCada + sobra).toFixed(2) : valorCada.toFixed(2);
        return { ...l, valor_previsto: v };
      });
    });
  }

  const somaTotal = linhas.reduce((s, l) => s + (parseFloat(l.valor_previsto) || 0), 0);
  const diff = somaTotal - valorAlvo;
  const fechado = Math.abs(diff) < 0.01;
  const semTitulo = linhas.some(l => !l.parcela_titulo.trim());
  const semValor = linhas.some(l => !l.valor_previsto || parseFloat(l.valor_previsto) <= 0);
  const dataValidaIso = (s: string) => {
    if (!s) return false;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m) return false;
    const y = +m[1], mo = +m[2], d = +m[3];
    const dt = new Date(y, mo - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
  };
  const linhaComEmissaoInvalida = linhas.findIndex(l => !dataValidaIso(l.nota_fiscal_emissao));
  const semEmissaoValida = linhaComEmissaoInvalida >= 0;
  const linhaComPrazoInvalido = linhas.findIndex(l => !Number.isFinite(l.prazo_dias) || l.prazo_dias < 0);
  const semPrazoValido = linhaComPrazoInvalido >= 0;
  // Check de parcelas SINALIZA, não trava (regra do plano FZ; caso 24053:
  // cliente arredondou 7 centavos pra baixo) — soma divergente pede
  // confirmação explícita em vez de impedir o salvar.
  const podeSalvar = !semTitulo && !semValor && !semEmissaoValida && !semPrazoValido && linhas.length > 0;

  async function salvar() {
    setErro('');
    if (!podeSalvar) {
      setErro(semTitulo ? 'Toda parcela precisa de título'
        : semValor ? 'Toda parcela precisa de valor > 0'
        : semEmissaoValida ? `Linha ${linhaComEmissaoInvalida + 1}: data de emissão inválida`
        : semPrazoValido ? `Linha ${linhaComPrazoInvalido + 1}: prazo deve ser ≥ 0 dias`
        : 'Adicione pelo menos 1 parcela');
      return;
    }
    if (!fechado) {
      modal.confirm({
        title: 'Soma difere do valor contratado',
        content: `A soma das parcelas (R$ ${somaTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}) difere do total contratado (R$ ${valorAlvo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}) em R$ ${diff.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}. Salvar mesmo assim? (ex.: arredondamento do cliente ou aditivo ainda não registrado)`,
        okText: 'Salvar mesmo assim',
        cancelText: 'Voltar e ajustar',
        onOk: () => executarSalvar(true),
      });
      return;
    }
    await executarSalvar(false);
  }

  async function executarSalvar(confirmarSomaDivergente: boolean) {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        {
          action: 'fin_reorganizar_parcelas',
          contrato_id: contrato.id,
          confirmar_soma_divergente: confirmarSomaDivergente,
          parcelas: linhas.map((l, i) => ({
            id: l.id,
            parcela_titulo: l.parcela_titulo.trim(),
            descricao: l.parcela_titulo.trim(),
            valor_previsto: parseFloat(l.valor_previsto),
            nota_fiscal_emissao: l.nota_fiscal_emissao || null,
            prazo_dias: Number.isFinite(l.prazo_dias) ? l.prazo_dias : 30,
            data_vencimento: l.data_vencimento || null,
            tipo_parcela: l.tipo_parcela || null,
            meio_pagamento: l.meio_pagamento || null,
            numero_parcela: l.numero_parcela || (i + 1),
            cliente_id: l.cliente_id ?? null,
            cnae: l.cnae ?? null,
            // Devolve cada linha ao contrato dela. O backend ignora isso em
            // linha existente (o UPDATE nunca mexe em contrato_id) e usa em
            // linha nova pra decidir onde ela nasce.
            contrato_id: l.contrato_id ?? contrato.id,
          })),
        },
        { headers: { Authorization: token } });
      message.success('Parcelamento reorganizado.');
      onSucesso();
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} width="min(1440px, 96vw)" confirmLoading={salvando}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <SettingOutlined /> Reorganizar parcelamento — {contrato.numero}
      </Typography.Title>}
      onOk={salvar}
      okText="Salvar reorganização"
      okButtonProps={{ disabled: !podeSalvar }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 11 }}>
        Edite valores, datas, títulos e o <strong>Faturar para</strong> (quando o
        CNPJ que recebe a nota difere do contratante). Soma diferente do
        contratado pede confirmação ao salvar (ex.: arredondamento do cliente).
        Parcelas já <strong>recebidas</strong> ficam travadas (histórico fiscal),
        exceto o CNPJ de faturamento, que segue editável.
        {linhas.some(l => l.contrato_numero) && (
          <> As parcelas marcadas com o número de um contrato-filho pertencem a
          ele e <strong>continuam nele</strong> ao salvar — o valor contratado
          total acima já soma o contrato e seus filhos.</>
        )}
      </Typography.Paragraph>

      {linhas.length === 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 10, fontSize: 12 }}
          message="Este contrato ainda não tem parcelas no plano"
          description={'A diferença abaixo é o valor contratado inteiro, porque não há '
            + 'nenhuma parcela pra comparar. Use "Adicionar linha" ou "Dividir igualmente" '
            + 'pra montar o parcelamento.'} />
      )}

      {foraDoReorganizador.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 10, fontSize: 12 }}
          message={`${foraDoReorganizador.length} parcela(s) do plano não entram aqui`}
          description={
            <>
              {foraDoReorganizador.map((p: any) => (
                <div key={p.id}>
                  <strong>{p.parcela_titulo || p.descricao}</strong> — {fmt(p.valor_previsto)}
                </div>
              ))}
              <div style={{ marginTop: 6 }}>
                Vieram do Conta Azul e aparecem na tabela, mas reorganizar não mexe nelas —
                seriam duplicadas. Elas continuam como estão, e a soma abaixo não as inclui.
              </div>
            </>
          } />
      )}

      <Card size="small"
        style={{ marginBottom: 10, background: fechado ? '#E8F5EE' : '#FFF7F7',
          borderColor: fechado ? '#0a7f4a' : '#B91626' }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="Valor contratado total" value={valorAlvo} prefix="R$" precision={0}
              valueStyle={{ fontSize: 16 }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
          </Col>
          <Col span={6}>
            <Statistic title="Soma das parcelas" value={somaTotal} prefix="R$" precision={0}
              valueStyle={{ fontSize: 16, color: fechado ? '#0a7f4a' : '#B91626' }}
              formatter={(v) => Number(v).toLocaleString('pt-BR')} />
          </Col>
          <Col span={6}>
            <Statistic title="Diferença"
              value={fechado ? 0 : Math.abs(diff)}
              precision={0} prefix={fechado ? '✓' : (diff > 0 ? '+R$' : '-R$')}
              valueStyle={{ fontSize: 16, color: fechado ? '#0a7f4a' : '#B91626' }}
              formatter={(v) => fechado ? 'Fechado' : Number(v).toLocaleString('pt-BR')} />
          </Col>
          <Col span={6}>
            <Statistic title="Parcelas" value={linhas.length} valueStyle={{ fontSize: 16 }} />
          </Col>
        </Row>
      </Card>

      {/* tableLayout fixed: sem isso a largura é ditada pelo CONTEÚDO, e um nome
          de cliente longo espremia título, emissão e valor até virarem "04/" e
          "9.0". Com fixed, as larguras abaixo valem de verdade e a rolagem
          horizontal cuida de telas estreitas. */}
      <div style={{ overflowX: 'auto', overflowY: 'visible', border: '0.5px solid #e0e0e0', borderRadius: 4, marginBottom: 10 }}>
        {/* A soma das colunas abaixo é 1376 — igual ao minWidth, pra não sobrar
            espaço nem espremer ninguém. Mexeu numa, ajuste o minWidth junto. */}
        <table style={{ width: '100%', minWidth: 1376, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 11 }}>
          <colgroup>
            <col style={{ width: 46 }} />{/* # */}
            <col style={{ width: 195 }} />{/* Título */}
            <col style={{ width: 220 }} />{/* Faturar para */}
            <col style={{ width: 122 }} />{/* Emissão */}
            <col style={{ width: 62 }} />{/* Prazo */}
            <col style={{ width: 98 }} />{/* Vencimento */}
            <col style={{ width: 112 }} />{/* Valor */}
            <col style={{ width: 72 }} />{/* % */}
            <col style={{ width: 44 }} />{/* Fixa */}
            <col style={{ width: 94 }} />{/* Tipo */}
            <col style={{ width: 99 }} />{/* Meio pgto */}
            <col style={{ width: 88 }} />{/* Cód. NF */}
            <col style={{ width: 86 }} />{/* Status */}
            <col style={{ width: 38 }} />{/* excluir */}
          </colgroup>
          <thead style={{ position: 'sticky', top: 0, background: '#F0F1F5', zIndex: 1 }}>
            <tr>
              <th style={{ padding: 8 }}>#</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Título</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Faturar para</th>
              <th style={{ padding: 8 }}>Emissão</th>
              <th style={{ padding: 8, textAlign: 'center' }}>Prazo</th>
              <th style={{ padding: 8 }}>Vencimento</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Valor</th>
              <th style={{ padding: 8, textAlign: 'right' }}>%</th>
              <th style={{ padding: 8, textAlign: 'center' }}>Fixa</th>
              <th style={{ padding: 8 }}>Tipo</th>
              <th style={{ padding: 8 }}>Meio pgto</th>
              <th style={{ padding: 8 }} title="Código de serviço da NF (CNAE)">Cód. NF</th>
              <th style={{ padding: 8 }}>Status</th>
              <th style={{ padding: 8 }}></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={l.id || `new-${i}`}
                style={{ borderBottom: '0.5px solid #f0f0f0', background: l.travada ? '#FAFAF8' : 'transparent' }}>
                <td style={{ padding: 4 }}>
                  <InputNumber size="small" value={l.numero_parcela}
                    onChange={(v) => setCampo(i, 'numero_parcela', v || (i + 1))}
                    style={{ width: '100%' }} />
                </td>
                <td style={{ padding: 4 }}>
                  <Input size="small" value={l.parcela_titulo}
                    onChange={e => setCampo(i, 'parcela_titulo', e.target.value)}
                    disabled={l.travada} placeholder="Ex.: BM 02"
                    title={l.parcela_titulo} />
                  {/* De qual contrato é a linha. Sem isso, editar a reorganização
                      do pai com as parcelas dos filhos juntas seria às cegas. */}
                  {l.contrato_numero && (
                    <Tag color="orange" style={{ fontFamily: 'monospace', fontSize: 9, marginTop: 3, marginRight: 0 }}
                      title="Esta parcela pertence a um contrato-filho e continua nele ao salvar">
                      {l.contrato_numero}
                    </Tag>
                  )}
                </td>
                <td style={{ padding: 4 }}>
                  {/* Editável mesmo em recebida (corrigir o CNPJ do tomador não
                      mexe em valor fiscal). `compacto` mostra só a razão social
                      quando fechado — o CNPJ segue no dropdown e na busca. */}
                  <SelectCliente size="small" compacto
                    value={l.cliente_id ?? null}
                    onChange={(v) => setCampo(i, 'cliente_id', v)}
                    contratanteId={(contrato as any).cliente_id ?? null}
                    placeholder="(contratante)" />
                </td>
                <td style={{ padding: 4 }}>
                  <DatePicker size="small" value={l.nota_fiscal_emissao && dataValidaIso(l.nota_fiscal_emissao) ? dayjs(l.nota_fiscal_emissao) : null}
                    onChange={(d) => setCampo(i, 'nota_fiscal_emissao', d ? d.format('YYYY-MM-DD') : '')}
                    disabled={l.travada} format="DD/MM/YYYY" style={{ width: '100%' }}
                    status={!l.travada && !dataValidaIso(l.nota_fiscal_emissao) ? 'error' : undefined} />
                </td>
                <td style={{ padding: 4 }}>
                  <InputNumber size="small" min={0} step={1} value={l.prazo_dias}
                    onChange={(v) => setCampo(i, 'prazo_dias', v || 0)}
                    disabled={l.travada} style={{ width: '100%' }} />
                </td>
                <td style={{ padding: 4 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {l.data_vencimento ? fmtData(l.data_vencimento) : '—'}
                  </Typography.Text>
                </td>
                <td style={{ padding: 4 }}>
                  <InputNumber size="small"
                    value={l.valor_previsto === '' || l.valor_previsto == null ? null : Number(l.valor_previsto)}
                    onChange={(v) => setCampo(i, 'valor_previsto', v != null ? String(v) : '')}
                    disabled={l.travada} step={100} min={0} style={{ width: '100%' }}
                    formatter={(val, info) => {
                      if (val == null || String(val) === '') return '';
                      if (info?.userTyping) return info.input;   // enquanto digita, mostra o cru (cursor não pula)
                      const n = Number(val);
                      return Number.isFinite(n) ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(val);
                    }}
                    parser={(val) => (val ? String(val).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, '') : '') as any} />
                </td>
                <td style={{ padding: 4 }}>
                  <InputNumber size="small" min={0} step={0.01}
                    value={valorAlvo > 0 ? ((parseFloat(l.valor_previsto) || 0) * 100 / valorAlvo) : 0}
                    onChange={(v) => setPct(i, v)}
                    disabled={l.travada || valorAlvo <= 0} style={{ width: '100%' }}
                    suffix="%" />
                </td>
                <td style={{ padding: 4, textAlign: 'center' }}>
                  {!l.travada && (
                    <Checkbox checked={!!l.fixada}
                      onChange={e => setCampo(i, 'fixada', e.target.checked)} />
                  )}
                </td>
                <td style={{ padding: 4 }}>
                  <Select size="small" value={l.tipo_parcela || undefined}
                    onChange={(v) => setCampo(i, 'tipo_parcela', v || '')}
                    placeholder="—" allowClear style={{ width: '100%' }}
                    options={[
                      { value: 'mobilizacao', label: '🚀 Mob' },
                      { value: 'medicao', label: '📐 Medição' },
                      { value: 'saldo', label: '✓ Saldo' },
                      { value: 'avulsa', label: '• Avulsa' },
                    ]} />
                </td>
                <td style={{ padding: 4 }}>
                  <Select size="small" value={l.meio_pagamento || undefined}
                    onChange={(v) => setCampo(i, 'meio_pagamento', v || '')}
                    placeholder="—" allowClear style={{ width: '100%' }}
                    options={[
                      { value: 'boleto', label: 'Boleto' },
                      { value: 'pix', label: 'PIX' },
                      { value: 'transferencia', label: 'Transf' },
                      { value: 'ted', label: 'TED' },
                    ]} />
                </td>
                <td style={{ padding: 4 }}>
                  {/* NF-CNAE: código de serviço da NF — herdado do serviço-fim, editável */}
                  <Select size="small" value={l.cnae || undefined}
                    onChange={(v) => setCampo(i, 'cnae', v || null)}
                    placeholder="—" allowClear style={{ width: '100%' }}
                    popupMatchSelectWidth={280}
                    options={[
                      { value: '07.03.01', label: '07.03.01', title: 'Estudos/planos de engenharia' },
                      { value: '07.03.02', label: '07.03.02', title: 'Projetos (anteprojeto/básico/executivo)' },
                      { value: '07.20.02', label: '07.20.02', title: 'Topografia terrestre' },
                      { value: '07.20.03', label: '07.20.03', title: 'Batimetria/hidrometria' },
                      { value: '7119-7/03', label: '7119-7/03', title: 'Desenho técnico' },
                    ]} />
                </td>
                {/* whiteSpace nowrap: "pendente" quebrava em "pendent / e" */}
                <td style={{ padding: 4, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  {l.travada ? <Tag color="green" style={{ fontSize: 9, margin: 0 }}>✓ recebida</Tag>
                    : l.status ? <Typography.Text type="secondary" style={{ fontSize: 10 }}>{l.status}</Typography.Text>
                    : <Typography.Text type="secondary" italic style={{ fontSize: 10 }}>nova</Typography.Text>}
                </td>
                <td style={{ padding: 4, textAlign: 'center' }}>
                  {!l.travada && (
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}
                      onClick={() => remover(i)} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Flex align="center" gap={8} style={{ marginBottom: 10 }}>
        <Button type="dashed" icon={<PlusOutlined />} onClick={adicionar}>Adicionar linha</Button>
        <Button disabled={linhas.length === 0} onClick={dividirIgualmente}
          style={{ borderColor: '#0930D0', color: '#0930D0' }}>
          ⚖ Dividir igualmente
        </Button>
        <Button disabled={linhas.length === 0} onClick={rebalancearNaoFixas}
          style={{ borderColor: '#B07500', color: '#B07500', background: '#FFF8E8' }}>
          🔄 Atualizar (rebalancear)
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>
          Fixe parcelas e clique <strong>Atualizar</strong> pra preencher as demais.
        </Typography.Text>
      </Flex>

      {erro && <Alert type="error" message={erro} showIcon style={{ marginBottom: 10 }} />}
    </Modal>
  );
}

// ════════════════ MODAL: MOVER PARCELA ════════════════

function ModalMoverParcela(props: {
  parcela: any; contratoAtual: Contrato;
  onSucesso: () => void; onClose: () => void;
}) {
  return <AntApp><ModalMoverParcelaInner {...props} /></AntApp>;
}

function ModalMoverParcelaInner({ parcela, contratoAtual, onSucesso, onClose }: {
  parcela: any; contratoAtual: Contrato;
  onSucesso: () => void; onClose: () => void;
}) {
  const { message } = AntApp.useApp();
  const [contratos, setContratos] = useState<Array<{ id: string; numero: string; titulo: string }>>([]);
  const [busca, setBusca] = useState('');
  const [destinoId, setDestinoId] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { (async () => {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_contratos', incluir_aditivos: true },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setContratos((Array.isArray(d) ? d : []).filter(c => c.id !== contratoAtual.id));
    } catch {}
  })(); }, [contratoAtual.id]);

  const filtrados = contratos.filter(c => {
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return c.numero.toLowerCase().includes(q) || (c.titulo || '').toLowerCase().includes(q);
  }).slice(0, 50);

  async function salvar() {
    if (!destinoId) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_mover_parcela', id: parcela.id, contrato_id_destino: destinoId },
        { headers: { Authorization: token } });
      message.success('Movida.');
      onSucesso();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={onClose} width={540}
      title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
        <SwapOutlined /> Mover parcela
      </Typography.Title>}
      onOk={salvar} confirmLoading={salvando} okText="Mover"
      okButtonProps={{ disabled: !destinoId }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Typography.Text strong>{parcela.parcela_titulo || parcela.descricao}</Typography.Text> · {fmt(parcela.valor_previsto)}
        <div><Typography.Text type="secondary" style={{ fontSize: 10 }}>de {contratoAtual.numero}</Typography.Text></div>
      </Card>

      <Input.Search allowClear placeholder="Buscar contrato (número ou título)..." autoFocus
        value={busca} onChange={e => setBusca(e.target.value)}
        prefix={<SearchOutlined />} style={{ marginBottom: 8 }} />

      <Radio.Group value={destinoId} onChange={(e) => setDestinoId(e.target.value)} style={{ width: '100%' }}>
        <List size="small" bordered style={{ maxHeight: 360, overflow: 'auto' }}
          dataSource={filtrados}
          locale={{ emptyText: busca ? 'Nenhum contrato encontrado' : 'Carregando...' }}
          renderItem={(c) => (
            <List.Item onClick={() => setDestinoId(c.id)} style={{ cursor: 'pointer',
              background: destinoId === c.id ? '#EFF3FF' : undefined }}>
              <Space>
                <Radio value={c.id} />
                <div>
                  <Typography.Text strong style={{ fontFamily: 'monospace', color: '#020D7F' }}>{c.numero}</Typography.Text>
                  <div><Typography.Text style={{ fontSize: 11, color: '#555' }}>{c.titulo}</Typography.Text></div>
                </div>
              </Space>
            </List.Item>
          )} />
      </Radio.Group>
    </Modal>
  );
}

// ════════════════ MODAL: LINK DO BM ════════════════

function ModalLinkBmProjeto({ link, onClose }: { link: { url: string; nome: string }; onClose: () => void }) {
  return <AntApp><ModalLinkBmProjetoInner link={link} onClose={onClose} /></AntApp>;
}

function ModalLinkBmProjetoInner({ link, onClose }: { link: { url: string; nome: string }; onClose: () => void }) {
  const { message } = AntApp.useApp();
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      message.warning('Selecione e copie o link manualmente');
    }
  }

  return (
    <Modal open onCancel={onClose} width={580} footer={[
      <Button key="close" onClick={onClose}>Fechar</Button>,
      <Button key="copy" type={copiado ? 'primary' : 'default'}
        icon={copiado ? <CheckOutlined /> : <CopyOutlined />}
        style={copiado ? { background: '#0a7f4a', borderColor: '#0a7f4a' } : { background: '#0930D0', borderColor: '#0930D0', color: '#fff' }}
        onClick={copiar}>
        {copiado ? 'Copiado' : 'Copiar link'}
      </Button>,
    ]} title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>
      📐 Link do BM
    </Typography.Title>}>
      <Typography.Paragraph><strong>{link.nome}</strong></Typography.Paragraph>
      <Card size="small" style={{ background: '#FAFBFF', borderColor: '#0930D0' }}>
        <Typography.Text style={{ fontFamily: 'monospace', fontSize: 11,
          wordBreak: 'break-all', color: '#020D7F' }}>{link.url}</Typography.Text>
      </Card>
    </Modal>
  );
}
